import React, { useState, useEffect, useMemo } from 'react';
import { FaUnlink, FaPencilAlt } from 'react-icons/fa';
import { Col, Row } from 'react-bootstrap';
import api from 'services/api';
import Card from 'v3/components/Card';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Fallback from 'v3/components/Fallback';
import DataTable from 'v3/components/DataTable';
import Badge from 'v3/components/Badge';
import theme from 'v3/theme';
import AddShippingCompany from './Modals/AddShippingCompany';
import UpdateShippingCompany from './Modals/UpdateShippingCompany';
import { useUser } from './context';

const userDetailsTableStyles = {
  borderCollapse: 'collapse',
  '> thead > tr > th': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> thead > tr > th:last-child': {
    textAlign: 'right',
    paddingRight: '24px',
  },
  '> tbody > tr': {
    borderBottom: '1px solid #E5E7EB',
  },
  '> tbody > tr > td:last-child': {
    textAlign: 'right',
    paddingRight: '24px',
  },
  '> tbody > tr:hover': {
    background: '#F5F6F8',
  },
  '> tbody > tr:hover > td': {
    background: '#F5F6F8',
  },
};

export default function UserShippingCompanies() {
  const user = useUser();
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [shippingCompanyId, setShippingCompanyId] = useState(null);

  const isEditing = useMemo(() => {
    return !!user.data?.id;
  }, [user.data?.id]);

  function handleDeleteShipper(shippingCompanyId) {
    user.setData({
      shipping_companies: user.data?.shipping_companies.filter(
        (shipper) => shipper.id !== shippingCompanyId,
      ),
    });
  }

  async function handleDettachShipper(shippingCompanyId) {
    try {
      setLoading(true);
      await api.delete(`v3/user/${user.data.id}/shipping-company/${shippingCompanyId}`);

      user.setData({
        shipping_companies: user.data?.shipping_companies.filter(
          (shipper) => shipper.id !== shippingCompanyId,
        ),
      });
    } catch (error) {
      user.showSnackbar('Erro ao excluir transportadora', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleRemoveShipper(shippingCompanyId) {
    if (isEditing) handleDettachShipper(shippingCompanyId);
    else handleDeleteShipper(shippingCompanyId);
  }

  const shippingCompaniesDataTable = useMemo(() => {
    const offset = (page - 1) * perPage;
    const shipper = user?.data?.shipping_companies
      ?.filter(
        (item) =>
          item.social_name.toLowerCase().includes(search.toLowerCase()) ||
          item.cgccpf.includes(search),
      )
      .slice(offset, perPage + offset);
    if (shipper?.length === 0 && page > 1) setPage(page - 1);
    return shipper;
  }, [user.data?.shipping_companies, page, perPage, search]);

  return (
    <>
      <AddShippingCompany isOpen={show} onClose={() => setShow(false)} />
      <UpdateShippingCompany
        isOpen={editing}
        onClose={() => setEditing(false)}
        shippingCompanyId={shippingCompanyId}
      />
      <Card
        loading={user.isFetching}
        header={
          <Text type="header" color="dark">
            Transportadoras
          </Text>
        }
        HeaderRightComponent={
          <>
            {!!user.data?.shipping_companies?.length && (
              <Button
                disabled={!user.hasPermissionToEdit}
                onClick={() => {
                  user.updatePrevUser();
                  setShow(true);
                }}
              >
                <Text type="regular" weight="500" color="white">
                  Adicionar
                </Text>
              </Button>
            )}
          </>
        }
      >
        <Row>
          <Fallback
            fall={!user.data?.shipping_companies?.length}
            component={
              <Col xs={12} className="align-items-center d-flex flex-column my-2">
                <Text color={theme.colors.dark}>
                  Não há transportadoras cadastradas nesse usuário.
                </Text>
                <Button
                  className="mt-4"
                  onClick={() => {
                    user.updatePrevUser();
                    setShow(true);
                  }}
                  disabled={!user.hasPermissionToEdit}
                >
                  <Text type="regular" weight="500" color="white">
                    Adicionar
                  </Text>
                </Button>
              </Col>
            }
          >
            <Col>
              <DataTable
                data={shippingCompaniesDataTable}
                total={user.data?.shipping_companies?.length}
                pages={Math.ceil(user.data?.shipping_companies?.length / perPage)}
                currentPage={page}
                onPageChanged={setPage}
                onSearchChanged={setSearch}
                onPerPageChanged={(v) => setPerPage(+v)}
                loading={loading}
                searchPlaceholder="CNPJ ou Razão social"
                tableStyles={userDetailsTableStyles}
                columns={[
                  {
                    name: 'Transportadoras',
                    selector: (row) => (
                      <>
                        <Text type="label" color="dark">
                          {row?.social_name}
                        </Text>
                        {row?.pivot?.is_headquarter && (
                          <Badge pill light variant="success" className="ml-2" size="sm">
                            <Text color="success" type="label">
                              Matriz
                            </Text>
                          </Badge>
                        )}
                      </>
                    ),
                  },
                  {
                    name: 'CPF/CNPJ',
                    selector: (row) => (
                      <Text type="label" color="dark">
                        {row?.cgccpf}
                      </Text>
                    ),
                  },
                  {
                    name: 'Ação',
                    render: (row) => (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mr-1"
                          disabled={!user.hasPermissionToEdit}
                          onClick={() => {
                            setShippingCompanyId(row.id);
                            setEditing(true);
                          }}
                        >
                          <FaPencilAlt size={16} color="#464E5F" />
                        </Button>
                        <Button
                          variant="error"
                          size="sm"
                          className="mx-1"
                          onClick={() => handleRemoveShipper(row.id)}
                          disabled={!user.hasPermissionToEdit}
                        >
                          <FaUnlink />
                        </Button>
                      </>
                    ),
                  },
                ]}
              />
            </Col>
          </Fallback>
        </Row>
      </Card>
    </>
  );
}
